import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

/**
 * `soft` (the default) is a status: a tinted fill and NO coloured border —
 * owner decision 2026-10-11. The border stays in the box model as
 * `border-transparent` so a soft badge and an outline one are the same size.
 *
 * `outline` is a plain label rather than a status — a quiet hairline tag on
 * the surface, e.g. `<Badge tone="neutral" variant="outline">`. A non-neutral
 * tone keeps its text colour so it still reads as that tone.
 */
export type BadgeVariant = 'soft' | 'outline';

const SOFT: Record<BadgeTone, string> = {
  neutral: 'border-transparent bg-surface-sunken text-fg-secondary',
  accent: 'border-transparent bg-accent-brand-soft text-accent-brand',
  success: 'border-transparent bg-success-soft text-success',
  warning: 'border-transparent bg-warning-soft text-warning',
  danger: 'border-transparent bg-danger-soft text-danger',
  info: 'border-transparent bg-info-soft text-info',
};

const OUTLINE: Record<BadgeTone, string> = {
  neutral: 'border-border bg-transparent text-fg-secondary',
  accent: 'border-border bg-transparent text-accent-brand',
  success: 'border-border bg-transparent text-success',
  warning: 'border-border bg-transparent text-warning',
  danger: 'border-border bg-transparent text-danger',
  info: 'border-border bg-transparent text-info',
};

const DOTS: Record<BadgeTone, string> = {
  neutral: 'bg-fg-subtle',
  accent: 'bg-accent-brand',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
};

export interface BadgeProps {
  tone?: BadgeTone;
  /** `soft` (default) for a status, `outline` for a plain label. */
  variant?: BadgeVariant;
  /**
   * Lets a long label wrap onto further lines inside a narrow cell instead of
   * running past it. Default `false`, so every existing badge stays on one
   * line. A prop rather than a class at the call site because it swaps the
   * baked-in `whitespace-nowrap`: two whitespace utilities race the emitted
   * stylesheet's order, so exactly one is emitted here. A single-line label
   * renders at the same height either way.
   */
  wrap?: boolean;
  /** Status dot. Tone alone must never be the only signal — keep the label too. */
  dot?: boolean;
  icon?: ReactNode;
  className?: string;
  /**
   * Native tooltip, for a FACT the short label cannot carry — a date behind
   * "From Xero", the full wording behind a truncated one.
   *
   * ⚠️ **Not a home for helper text.** The standing prohibition covers advice
   * and explanation wherever it renders, and a `title` is not an exemption from
   * it. What belongs here is a value the reader would otherwise have to go and
   * look up, which is the same exception a truncated table cell already relies
   * on. It is also invisible on touch, so nothing a reader NEEDS may live here
   * alone.
   */
  title?: string;
  children: ReactNode;
}

export function Badge({
  tone = 'neutral',
  variant = 'soft',
  wrap = false,
  dot,
  icon,
  className,
  title,
  children,
}: BadgeProps) {
  return (
    <span
      title={title}
      className={cx(
        'inline-flex items-center gap-1.5 rounded-sm border px-1.5 py-0.5 text-2xs font-medium',
        wrap ? 'max-w-full whitespace-normal [overflow-wrap:anywhere]' : 'whitespace-nowrap',
        variant === 'outline' ? OUTLINE[tone] : SOFT[tone],
        className,
      )}
    >
      {dot && <span aria-hidden="true" className={cx('h-1.5 w-1.5 rounded-full', DOTS[tone])} />}
      {icon}
      {children}
    </span>
  );
}
